import React, { useState, useEffect } from 'react';
import { Pencil, Loader2, Save, X, Minus, Plus, AlertTriangle } from 'lucide-react';
import { Button } from './ui/button';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;
const META = ['Item ID', 'SKU ID', 'Spec ID'];
const money = (v) => (v || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const label = (v) => (typeof v === 'string' ? v : v.en);
const valueForOrder = (v) => (typeof v === 'string' ? v : (v.en !== v.zh ? `${v.en} (${v.zh})` : v.zh));

export default function OrderEditor({ order, authHeaders, onSaved, onClose }) {
  const [loading, setLoading] = useState(true);
  const [options, setOptions] = useState(null);
  const [qty, setQty] = useState(order.quantity);
  const [notes, setNotes] = useState(order.special_instructions || '');
  const [picked, setPicked] = useState({});
  const [saving, setSaving] = useState(false);
  const current = order.sku_selections?.filter((s) => !META.includes(s.name)) || [];

  useEffect(() => {
    fetch(`${API}/api/orders/${order.id}/product-options`, { headers: authHeaders() })
      .then((r) => r.json())
      .then((d) => {
        if (d.success && d.data) {
          setOptions(d.data);
          const init = {};
          d.data.sku_options?.forEach((opt) => {
            const cur = current.find((s) => s.name === opt.name);
            const match = cur && opt.values.find((v) => valueForOrder(v) === cur.value || label(v) === cur.value);
            if (match) init[opt.name] = label(match);
          });
          setPicked(init);
        }
      })
      .catch(() => {})
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [order.id]);

  const matched = options?.sku_list?.find((s) => { const keys = Object.keys(s.props || {}); return keys.length > 0 && keys.every((k) => picked[k] === s.props[k]); });
  const tier = (options?.price_tiers || []).filter((t) => qty >= t.min_qty).sort((a, b) => b.min_qty - a.min_qty)[0]?.price_rmb;
  const unit = matched?.price_rmb || tier || options?.price_rmb || order.price_rmb;
  const total = unit * qty + (order.domestic_shipping_rmb || 0);
  const allPicked = !options?.sku_options?.length || options.sku_options.every((o) => picked[o.name]);

  const save = async () => {
    if (!allPicked) return toast.error('Please choose a value for every option');
    setSaving(true);
    try {
      const sku_selections = options?.sku_options?.length
        ? options.sku_options.map((opt) => { const v = opt.values.find((x) => label(x) === picked[opt.name]); return { name: opt.name, value: v ? valueForOrder(v) : picked[opt.name] }; })
        : current;
      const res = await fetch(`${API}/api/orders/${order.id}`, { method: 'PATCH', headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify({ quantity: qty, special_instructions: notes, sku_selections, sku_id: matched?.sku_id || null, spec_id: matched?.spec_id || null }) });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Could not update order');
      toast.success(`Order #${order.id} updated — new total ¥${money(data.data.total_rmb)}. Your proforma invoice now reflects this.`);
      onSaved(data.data);
    } catch (err) { toast.error(err.message); }
    finally { setSaving(false); }
  };

  return (
    <div className="rounded-xl border-2 border-orange-200 bg-orange-50/60 p-4 space-y-4" data-testid={`order-editor-${order.id}`}>
      <div className="flex items-center justify-between">
        <h4 className="font-bold text-gray-900 flex items-center gap-2 text-sm"><Pencil className="w-4 h-4 text-orange-600" /> Edit order #{order.id}</h4>
        <button type="button" onClick={onClose} className="text-gray-400 hover:text-gray-700" aria-label="Close editor" data-testid={`order-editor-close-${order.id}`}><X className="w-5 h-5" /></button>
      </div>

      {loading ? <div className="flex items-center gap-2 text-sm text-gray-600"><Loader2 className="w-4 h-4 animate-spin" /> Loading live options & prices from the listing…</div> : (
        <>
          {!options && (
            <p className="text-xs text-amber-800 bg-amber-100 border border-amber-200 rounded-lg p-2 flex items-center gap-2" data-testid={`order-editor-offline-${order.id}`}>
              <AlertTriangle className="w-4 h-4" /> Live listing data is unavailable right now — you can still change the quantity and instructions; options stay as selected.
            </p>
          )}
          {options?.sku_options?.map((opt) => (
            <div key={opt.name}>
              <span className="text-xs font-semibold text-gray-600">{opt.name}{opt.name_zh && opt.name_zh !== opt.name ? ` · ${opt.name_zh}` : ''}</span>
              <div className="flex flex-wrap gap-1.5 mt-1" data-testid={`order-editor-option-${order.id}-${opt.name}`}>
                {opt.values.map((v) => { const l = label(v); const active = picked[opt.name] === l; return (
                  <button key={l} type="button" onClick={() => setPicked((p) => ({ ...p, [opt.name]: l }))}
                    className={`px-2.5 py-1 rounded-lg border text-xs transition-colors ${active ? 'bg-orange-600 border-orange-600 text-white' : 'bg-white border-gray-200 text-gray-700 hover:border-orange-400'}`}
                    data-testid={`order-editor-value-${order.id}-${l}`}>{l}</button>
                ); })}
              </div>
            </div>
          ))}

          <div className="flex flex-col sm:flex-row sm:items-end gap-4">
            <div>
              <span className="text-xs font-semibold text-gray-600 block mb-1">Quantity</span>
              <div className="flex items-center border border-gray-200 rounded-lg overflow-hidden bg-white w-max">
                <button type="button" onClick={() => setQty(Math.max(1, qty - 1))} className="px-3 py-2 hover:bg-gray-100 text-gray-600" data-testid={`order-editor-qty-minus-${order.id}`}><Minus className="w-4 h-4" /></button>
                <input type="number" min="1" value={qty} onChange={(e) => setQty(Math.max(1, Number(e.target.value) || 1))} className="w-20 text-center py-2 border-x border-gray-200 text-sm font-semibold focus:outline-none" data-testid={`order-editor-qty-${order.id}`} />
                <button type="button" onClick={() => setQty(qty + 1)} className="px-3 py-2 hover:bg-gray-100 text-gray-600" data-testid={`order-editor-qty-plus-${order.id}`}><Plus className="w-4 h-4" /></button>
              </div>
              {options?.moq_qty > 1 && qty < options.moq_qty && <p className="text-[11px] text-amber-700 mt-1">Supplier MOQ is {options.moq_qty}</p>}
            </div>
            <div className="flex-1">
              <span className="text-xs font-semibold text-gray-600 block mb-1">Special instructions</span>
              <input value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Color, logo printing, packaging…" className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm bg-white focus:border-orange-500 focus:outline-none" data-testid={`order-editor-notes-${order.id}`} />
            </div>
          </div>

          <div className="bg-white rounded-lg border border-gray-200 p-3 text-sm space-y-1" data-testid={`order-editor-summary-${order.id}`}>
            <div className="flex justify-between text-gray-600"><span>Unit price{matched ? ' (selected SKU)' : tier ? ' (wholesale tier)' : ''}</span><span>¥{money(unit)}</span></div>
            <div className="flex justify-between text-gray-600"><span>Quantity</span><span>× {qty}</span></div>
            {order.domestic_shipping_rmb > 0 && <div className="flex justify-between text-gray-600"><span>Domestic shipping</span><span>¥{money(order.domestic_shipping_rmb)}</span></div>}
            <div className="flex justify-between font-bold text-gray-900 border-t border-gray-100 pt-1"><span>New total</span><span className="text-orange-600" data-testid={`order-editor-total-${order.id}`}>¥{money(total)}</span></div>
            {Math.abs(total - order.total_rmb) > 0.004 && <p className="text-[11px] text-gray-500">Was ¥{money(order.total_rmb)} · proforma invoice and payment reference update instantly</p>}
          </div>

          <div className="flex gap-2">
            <Button onClick={save} disabled={saving} className="flex-1 bg-orange-600 hover:bg-orange-700 text-white font-bold rounded-xl" data-testid={`order-editor-save-${order.id}`}>
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />} Save changes
            </Button>
            <Button variant="outline" onClick={onClose} className="rounded-xl" data-testid={`order-editor-cancel-${order.id}`}>Cancel</Button>
          </div>
        </>
      )}
    </div>
  );
}
